import { useState } from 'react';
import { Alert, Box, GlobalStyles, Typography } from '@mui/material';
import { alpha, createTheme, ThemeProvider } from '@mui/material/styles';

import { consumeQueryParam } from '../lib/query-params';
import { useAuthTranslation } from '../i18n/auth-i18n';
import { AtmosphericGlow } from './AtmosphericGlow';

// The auth surface is intentionally dark and brand-forward — a "front door"
// that stays dark regardless of the app's light/dark mode. Scoping a dark theme
// here forces every nested input/button/text dark and gives the card true dark
// glass (rather than inheriting a light surface from the app).
const authDarkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#6271F8', contrastText: '#0B0A12' },
    background: { default: '#0B0A12', paper: '#16131F' },
    text: { primary: '#F4F2F8', secondary: 'rgba(244, 242, 248, 0.62)' },
    divider: 'rgba(255, 255, 255, 0.1)',
  },
  typography: {
    fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
    h4: { fontSize: '1.125rem', fontWeight: 700, letterSpacing: '-0.01em' }, // 18px titles (Linear-compact)
    body1: { fontSize: '0.8125rem' },
    body2: { fontSize: '0.8125rem' }, // 13px body text
    caption: { fontSize: '0.75rem' }, // 12px helper
    button: { fontSize: '0.8125rem' }, // 13px
  },
  components: {
    MuiInputBase: { styleOverrides: { input: { fontSize: '0.8125rem' } } }, // 13px field text
  },
  shape: { borderRadius: 8 }, // inputs/buttons land at 12px (1.5×), under the card's 16px
});

export interface AuthLayoutProps {
  /** Logo element (image + typography allowed). */
  logo: React.ReactElement;
  /** Brand subtitle below the logo. Omit to show the logo alone. */
  brandingSubtitle?: string;
  /** Page-specific content (form, buttons, links). */
  children: React.ReactNode;
  /** Optional test id on the card container. */
  'data-testid'?: string;
  /**
   * Resolved error code, when the caller already looked past the top-level
   * `?error=` — e.g. LoginPage's nested `?redirect=...%3Ferror%3D...` shape.
   * Overrides the value AuthLayout reads itself. Leave unset to let AuthLayout
   * read (and strip) `?error=` from the URL as usual.
   */
  errorCode?: string | null;
}

// Maps an OAuth callback `?error=` code to its localized i18n key. Unmapped
// codes fall back to auth.errors.oauthGeneric with the raw code interpolated.
const AUTH_ERROR_KEYS: Record<string, string> = {
  invalid_state: 'auth.errors.oauthStateMismatch',
  session_cookie_disabled: 'auth.errors.oauthSessionFailed',
  auth_failed: 'auth.errors.oauthFailed',
  no_token: 'auth.errors.oauthNoToken',
  missing_code: 'auth.errors.oauthMissingCode',
  access_denied: 'auth.errors.oauthAccessDenied',
};

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

/** Entrance — opacity + translateY, custom ease-out, `both` fill. Snappy (320ms)
 * with a tiny logo→card stagger; the card rises as one piece so its contents
 * never make you wait (the old 520ms + 210ms inner stagger read as sluggish). */
const rise = (delayMs: number) => ({
  animation: `ariRise 320ms ${EASE_OUT} ${delayMs}ms both`,
});

// One-off fractal-noise tile for a faint film-grain texture.
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

export function AuthLayout({
  logo,
  brandingSubtitle,
  children,
  'data-testid': testId,
  errorCode: errorCodeProp,
}: AuthLayoutProps) {
  const { t } = useAuthTranslation();
  // Read synchronously during initial render — useOAuthCallback only cleans
  // connector params, leaving ?error= for us to consume here. Resolve the
  // message at render so it follows the active language. Always consumed, even
  // when a caller overrides the value below, so the top-level param is still
  // stripped from the URL.
  const [consumedErrorCode] = useState<string | null>(() => consumeQueryParam('error', ['error_description']));
  const errorCode = errorCodeProp !== undefined ? errorCodeProp : consumedErrorCode;
  const [dismissed, setDismissed] = useState(false);

  const authError =
    !dismissed && errorCode
      ? AUTH_ERROR_KEYS[errorCode]
        ? t(AUTH_ERROR_KEYS[errorCode])
        : t('auth.errors.oauthGeneric', { error: errorCode })
      : null;

  return (
    <ThemeProvider theme={authDarkTheme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
          backgroundColor: 'background.default',
          padding: { xs: 2.5, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <GlobalStyles
          styles={{
            '@keyframes ariRise': {
              from: { opacity: 0, transform: 'translateY(10px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
            '@media (prefers-reduced-motion: reduce)': {
              '@keyframes ariRise': { from: { opacity: 0 }, to: { opacity: 1 } },
            },
          }}
        />

        {/* Hero gradient glow — our blue-toned glow rising from the bottom. Most
            of the bright ellipse sits below the viewport so the center stays
            dark. */}
        <AtmosphericGlow animate />

        {/* Faint grain for texture. */}
        <Box
          aria-hidden
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            backgroundImage: GRAIN,
            opacity: 0.035,
            mixBlendMode: 'soft-light',
          }}
        />

        {/* Logo / branding */}
        <Box sx={{ mb: { xs: 3, sm: 3.5 }, textAlign: 'center', position: 'relative', zIndex: 1, ...rise(0) }}>
          <Box sx={{ mb: brandingSubtitle ? 1 : 0, display: 'flex', justifyContent: 'center' }}>{logo}</Box>
          {brandingSubtitle && (
            <Typography variant="body1" color="text.secondary">
              {brandingSubtitle}
            </Typography>
          )}
        </Box>

        {/* Glass card — true dark glass with a soft shadow, no stroke. */}
        <Box
          data-testid={testId}
          sx={(theme) => ({
            width: '100%',
            maxWidth: { xs: '100%', sm: 420 },
            backgroundColor: '#171717',
            borderRadius: '16px',
            padding: { xs: 3, sm: 4 },
            boxShadow: `0 1px 0 ${alpha(theme.palette.common.white, 0.05)} inset, 0 24px 60px -24px ${alpha(
              '#000',
              0.85
            )}`,
            position: 'relative',
            zIndex: 1,
            ...rise(70),
          })}
        >
          {authError && (
            <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setDismissed(true)}>
              {authError}
            </Alert>
          )}

          {children}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
